import { useAuthenticationContext } from "@dbn/omnichannel-design-system-g4p";
import { option } from "fp-ts";
import { useAppContext } from "../useAppContext";
import { HeaderParam } from "./documentAPI";
import { constant, pipe } from "fp-ts/function";
import { Option } from "fp-ts/Option";
import { removeDiacritics } from "./CompanyField/utils";

export type DeepLink = "Authorize" | "Exit" | "ExitHome";

export const toBase64 = (str: string) => {
  const utf8Bytes = new TextEncoder().encode(str);
  return btoa(String.fromCharCode(...utf8Bytes));
};

export const encodeUri = (str: string) => {
  return str
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "")
    .replace(/—|–/g, "-")
    .replace(/-+/g, "-")
    .replace(/\s/g, "%20");
};

export const normalizeFileName = (str: string) => {
  return str
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/—|–/g, "-")
    .replace(/-+/g, "-");
};

export const toBase64Url = (str: string) => {
  const b64 = toBase64(str);
  return encodeUri(b64);
};

export function downloadWithDeepLink(
  url: string,
  mimetype: string,
  filename: string,
  headers: Array<HeaderParam>,
  token: Option<string>
) {
  return () => {
    const headersDeepLink = headers.reduce((result, item) => {
      if (item.name === "fileName") {
        return `${result}header=${toBase64Url(
          item.name + "=" + normalizeFileName(removeDiacritics(item.value))
        )}&`;
      }
      return `${result}header=${toBase64Url(item.name + "=" + item.value)}&`;
    }, "");

    const headerAuthorization = pipe(
      token,
      option.map(
        token => "&header=" + toBase64Url("Authorization=Bearer " + token)
      ),
      option.getOrElse(constant(""))
    );

    const deepLink = `mb-unicredit://filedownload/post?url=${toBase64Url(
      url
    )}&${headersDeepLink}mimetype=${toBase64Url(mimetype)}&filename=${encodeUri(
      removeDiacritics(filename)
    )}${headerAuthorization}`;

    console.log("Called deepLink: ", deepLink);
    window.location.href = deepLink;

    return "";
  };
}

export function downloadWithDeepLinkGet(
  url: string,
  mimetype: string,
  filename: string
) {
  return () => {
    const deepLink = `mb-unicredit://filedownload/get?url=${toBase64Url(
      url
    )}&mimetype=${toBase64Url(mimetype)}&filename=${encodeUri(
      removeDiacritics(filename)
    )}`;

    console.log("Called deepLink: ", deepLink);
    window.location.href = deepLink;
  };
}

export function useSBDeepLink(link: DeepLink, biometric: boolean = false) {
  const { authInfo } = useAuthenticationContext();

  const {
    config: { exitMBHomeRedirectUrl },
  } = useAppContext();

  const url = (() => {
    switch (link) {
      case "Authorize":
        if (option.isNone(authInfo)) {
          throw new Error(
            "Trying to call native app for authorization without a flow ID"
          );
        }
        const flowId = authInfo.value.flowId;

        return `mb-unicredit://sign/query?section=DBN&reference=${flowId}&biometric=${biometric}`;
      case "Exit":
        return "mb-unicredit://close/query?appSection=online:menu:more";
      case "ExitHome":
        return exitMBHomeRedirectUrl;
    }
  })();

  return () => {
    window.location.href = url;
    return "";
  };
}

export function useAppRedirect() {
  const {
    apiParameters: { tenant },
    config: { environment },
  } = useAppContext();

  const redirectUrl =
    (() => {
      switch (environment) {
        case "sit":
        case "uat":
          return ["mb", "unicredit", tenant, environment].join("-");
        case "dev":
        case "ci":
        case "hf":
        case "prod":
          return ["mb", "unicredit", tenant].join("-");
      }
    })() + "://appredirect";

  return () => (window.location.href = redirectUrl);
}
